import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Grid } from "@mui/material";
import TextField from "../common/TextField";
import Button from "../common/Button";
import Table from "../common/tableWithBackEndPagination";
import ConfirmDialoag from "../common/ConfirmDialoag";
import Loader from "../../Layout/Loader";
import ExportImportMenu from "../common/ExportImportMenu";
import { checkPrivileges, PaginationConfig } from "../../Helpers";
import { getAccounts } from "../../Actions/Accounts";
import { parentUsersLimit } from "../../Actions/Users";
import { parentUnitsLimit } from "../../Actions/Devices";
import withRoutes from "../../HOC/HocRoutes";
import ContractTypeTabs from "../identity/ContractTypeTabs";
import { contractTypeFromTab } from "../identity/formDefaults";
import { accountRowDefinition, setPagination } from "../identity/tables";
import CreateUpdateForm from "./CreateUpdateForm";
import AccountDetailView from "./AccountDetailView";
import {
  activateAccount,
  countDevices,
  countUsers,
  deleteAccount,
  handleIdentityError,
  listAccounts,
} from "./accountsApi";

const userTypeLabel = (userType) =>
  ({
    1: "User",
    2: "Admin",
    3: "Account Admin",
    4: "Service Provider",
    5: "Reseller",
    6: "Distributor",
  }[userType] || "");

const reduceData = (rows = []) =>
  rows
    .filter((item) => item.userType !== -1)
    .map((item, index) => ({
      Srn: index + 1,
      Account_Name: item.name,
      Email: item.email,
      Account_Type: userTypeLabel(item.userType),
      Contract_Name: item.contractName,
      Contract_Number: item.contractNumber,
      PAC_Contract_Number: item.pacContractId,
      Credit_Limit: item.creditLimit,
      Credit_Period: item.creditPeriod,
      Account_Status: item.active ? "Active" : "InActive",
      Tax_Number: item.attributes?.c_taxNumber,
      Contract_Start: item.contractStartDate
        ? new Date(item.contractStartDate).toLocaleDateString("en-GB")
        : null,
      Contract_End: item.contractEndDate
        ? new Date(item.contractEndDate).toLocaleDateString("en-GB")
        : null,
    }));

const AccountListInner = (props) => {
  const dispatch = useDispatch();
  const logInUser = useSelector((state) => state.logInUsers);
  const themecolors = useSelector((state) => state.themecolors);
  const accounts = useSelector((state) => state.accounts);
  const parentUserLimits = useSelector((state) => state.parentUserLimits);

  const [mode, setMode] = useState("list");
  const [tabValue, setTabValue] = useState("maintenance");
  const [listState, setListState] = useState({
      ...PaginationConfig,
    tableData: [],
    pagData: null,
    loading: false,
  });
  const [selectedAccount, setSelectedAccount] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);

  const loadAccounts = useCallback(async () => {
    if (!logInUser?.id) return;
    setListState((prev) => ({ ...prev, loading: true }));
    try {
      const result = await listAccounts({
        userId: logInUser.id,
        page: listState.page,
        pageSize: listState.pageSize,
        search: listState.itemSearch,
        tab: tabValue,
      });
      dispatch(getAccounts(result));
      setListState((prev) => ({
        ...prev,
        tableData: result.data || [],
        pagData: result,
        loading: false,
      }));
    } catch (error) {
      toast.error(handleIdentityError(error, props.translate));
      setListState((prev) => ({ ...prev, loading: false, tableData: [], pagData: null }));
    }
  }, [
    logInUser,
    listState.page,
    listState.pageSize,
    listState.itemSearch,
    tabValue,
    dispatch,
    props.translate,
  ]);

  useEffect(() => {
    if (mode === "list" && logInUser?.id) loadAccounts();
  }, [
    mode,
    logInUser?.id,
    listState.page,
    listState.pageSize,
    listState.itemSearch,
    tabValue,
  ]);

  useEffect(() => {
    if (!logInUser?.id) return;
    countUsers(logInUser.id)
      .then((data) => dispatch(parentUsersLimit(data)))
      .catch(() => {});
    countDevices(logInUser.id)
      .then((data) => dispatch(parentUnitsLimit(data)))
      .catch(() => {});
  }, [logInUser, dispatch]);

  const goList = () => {
    setMode("list");
    setSelectedAccount("");
  };

  const pickRow = (selected) => (Array.isArray(selected) ? selected[0] : selected);
  const canCreate =
    checkPrivileges("accountCreate") &&
    checkPrivileges("userCreate") &&
    (parentUserLimits?.remaningLimit > 0 || parentUserLimits?.remaningLimit === -1);

    return (
    <div>
      {mode === "create" && (
        <CreateUpdateForm
          translate={props.translate}
          onCancel={goList}
          onCreated={goList}
        />
      )}

      {mode === "detail" && selectedAccount && (
        <AccountDetailView
          selectItemName={selectedAccount.name}
          selectItemId={selectedAccount.userId}
          selectAccountId={selectedAccount.id}
          selectItem={selectedAccount}
          getMoreAccounts={loadAccounts}
          onClose={goList}
          translate={props.translate}
          value={tabValue}
        />
      )}

      {mode === "list" && (
          <>
            <Grid container style={{ display: "flex" }}>
            <ContractTypeTabs
              value={tabValue}
              onChange={(tab) => {
                setTabValue(tab);
                setListState((prev) => ({ ...prev, page: 1 }));
              }}
              translate={props.translate}
              themecolors={themecolors}
              widths={{ maintenance: "80%", leasing: "50%", cashCustomer: "75%" }}
            />
            <Grid item xs={7} sm={7} style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ width: "25%", marginRight: 20 }}>
                  <TextField
                    fullWidth
                  label={props.translate("search")}
                  value={listState.itemSearch}
                  onChange={(e) =>
                    setListState((prev) => ({ ...prev, itemSearch: e.target.value, page: 1 }))
                  }
                  />
                </div>
                <div style={{ width: "10%", marginRight: 5, marginTop: 6 }}>
                  <Button
                  disabled={!canCreate}
                    aria-label="Create"
                    size="small"
                    color="inherit"
                  onClick={() => setMode("create")}
                    id="createButton"
                  >
                  {props.translate("create")}
                  </Button>
                </div>
                <div style={{ width: "10%", marginRight: 5, marginTop: 6 }}>
                  <ExportImportMenu
                    fileName="uploadAccount"
                    isUploadAccount
                    isServices
                  themecolors={themecolors}
                  translate={props.translate}
                  title={props.translate("reportExport")}
                    downloadType="accounts"
                  Data={reduceData(listState.tableData)}
                  DataLength={listState.tableData.length > 0}
                  contractType={contractTypeFromTab(tabValue)}
                  page={listState.page}
                  limit={listState.pageSize}
                  closedModel={goList}
                  searchValue={listState.itemSearch}
                  dispatch={dispatch}
                    noPdf
                  />
                </div>
              </Grid>
            </Grid>
          {listState.loading && !listState.tableData.length ? (
              <Loader />
            ) : (
              <Table
              translate={props.translate}
              rows={listState.tableData}
              pagination={setPagination(
                listState.pagData,
                listState.pageSize,
                listState.tableData.length,
              )}
              logInUser={logInUser}
              handleChangeRowsPerPage={(pageSize) =>
                setListState((prev) => ({ ...prev, pageSize, page: 1 }))
              }
              handleChangePage={(page) => setListState((prev) => ({ ...prev, page }))}
              SearchItem={(value) =>
                setListState((prev) => ({ ...prev, itemSearch: value, page: 1 }))
              }
              onClick={(item) => {
                setSelectedAccount(pickRow(item));
                setMode("detail");
              }}
              onDelete={
                checkPrivileges("accountDelete")
                  ? (item) => setDeleteTarget(pickRow(item))
                  : ""
              }
              handleAccountStatus={() => {}}
              openAccountStausDialog={(event, item) => {
                event.stopPropagation();
                setStatusTarget(pickRow(item));
              }}
              btnName={props.translate("create")}
              onCreate={() => setMode("create")}
              btnName1="accounts"
                rowsPerPage={15}
              themecolors={themecolors}
                hasAccessOfUpdate
                filterDropdown
                setSerialNo
                isCursoPointer
              rowDefinition={accountRowDefinition(props.translate)}
              />
            )}
          </>
        )}

      {deleteTarget && (
          <ConfirmDialoag
          onCancel={() => setDeleteTarget(null)}
          onOk={async () => {
            try {
              await deleteAccount(deleteTarget);
              toast.success(props.translate("userIsRemoved"));
              setDeleteTarget(null);
              loadAccounts();
            } catch (error) {
              toast.error(handleIdentityError(error, props.translate));
            }
          }}
          title={props.translate("areYouWantToDelete")}
          children={deleteTarget.name}
          translate={props.translate}
        />
      )}

      {statusTarget && (
          <ConfirmDialoag
          translate={props.translate}
          onCancel={() => setStatusTarget(null)}
          onOk={async () => {
            try {
              await activateAccount(statusTarget.id, statusTarget.active);
              toast.success(
                `Account ${statusTarget.active ? "Deactivated" : "Activated"}`,
              );
              setStatusTarget(null);
              loadAccounts();
            } catch (error) {
              toast.error(handleIdentityError(error, props.translate));
            }
          }}
          title={
            statusTarget.active
              ? "Are You Sure, You Want to DeActivate"
              : "Are You Sure, You Want to Activate"
          }
            children={
              <span>
                <span style={{ fontWeight: "bold" }}>Account Name : </span>
              {statusTarget.name}
              </span>
            }
          />
        )}
    </div>
  );
};

export const AccountModal = withRoutes(AccountListInner);
export default AccountModal;
